<script>
import { mapGetters, mapActions } from 'vuex'

export default {
    name: 'ModalManager',

    data() {
        return {
            loadedComponent: null,
            isLoading: false,
        }
    },

    computed: {
        ...mapGetters('modal', ['isModalOpen', 'currentModal']),
    },

    watch: {
        isModalOpen: {
            handler(isOpen) {
                if (isOpen) {
                    this.lockBodyScroll()
                    this.loadModalComponent()
                    this.addEscapeListener()
                } else {
                    this.unlockBodyScroll()
                    this.loadedComponent = null
                    this.removeEscapeListener()
                }
            },
            immediate: true,
        },
    },

    beforeDestroy() {
        this.unlockBodyScroll()
        this.removeEscapeListener()
    },

    methods: {
        ...mapActions('modal', ['hideModal']),

        async loadModalComponent() {
            if (!this.currentModal.component) {
                console.error('No component found in currentModal')
                return
            }

            this.isLoading = true
            try {
                const module = await this.currentModal.component()
                this.loadedComponent = module.default
            } catch (error) {
                console.error('Failed to load modal component:', error)
                this.hideModal()
            } finally {
                this.isLoading = false
            }
        },

        lockBodyScroll() {
            const scrollY = window.scrollY
            document.body.style.position = 'fixed'
            document.body.style.top = `-${scrollY}px`
            document.body.style.width = '100%'
            document.body.dataset.scrollY = scrollY
        },

        unlockBodyScroll() {
            const scrollY = document.body.dataset.scrollY || '0'
            document.body.style.position = ''
            document.body.style.top = ''
            document.body.style.width = ''
            window.scrollTo(0, parseInt(scrollY, 10))
            delete document.body.dataset.scrollY
        },

        handleEscape(event) {
            if (event.key === 'Escape' && this.isModalOpen) {
                this.hideModal()
            }
        },

        addEscapeListener() {
            document.addEventListener('keydown', this.handleEscape)
        },

        removeEscapeListener() {
            document.removeEventListener('keydown', this.handleEscape)
        },

        handleBackgroundClick() {
            this.hideModal()
        },
    },
}
</script>

<template>
    <transition name="modal-fade">
        <div
            v-if="isModalOpen"
            class="modal-container"
        >
            <div
                class="modal-overlay"
                @click="handleBackgroundClick"
            />
            <div
                v-if="!isLoading && loadedComponent"
                class="modal-wrapper"
                role="dialog"
                aria-modal="true"
            >
                <component
                    :is="loadedComponent"
                    :data="currentModal.data"
                    @close="hideModal"
                />
            </div>
        </div>
    </transition>
</template>

<style scoped lang="scss">
@use '@design' as *;

.modal-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: $layer-modal-z-index;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: $s-m;

    @include mobileOnly {
        padding: $s-l;
    }
}

.modal-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.75);
    cursor: pointer;
}

.modal-wrapper {
    position: relative;
    z-index: 1;
    max-width: 100%;
    max-height: 100%;
    overflow: auto;

    @include mobileOnly {
        width: auto; // Auto width to allow the modal to size itself
        height: auto; // Auto height
        display: flex;
        align-items: center;
        justify-content: center;
    }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
    opacity: 0;
}

.modal-fade-enter-active .modal-wrapper,
.modal-fade-leave-active .modal-wrapper {
    transition: transform 0.2s ease;
}

.modal-fade-enter .modal-wrapper,
.modal-fade-leave-to .modal-wrapper {
    transform: scale(0.95);
}
</style>
